import { useMemo } from 'react';
import { Stack } from '@mui/material';
import { ENTITY_TYPES } from '@vy/integrations-types';
import { buildCurrencyLevel, convertCurrencyToUsd } from '@vy/services';
import type { Customer } from '@vy/types';
import { isEmptyArray, isNotEmptyArray, isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import { useCreditLedgerOverview } from '../../../../../../../hooks/requests/use-credit-ledger-overview';
import { useExchangeRates } from '../../../../../../../hooks/requests/exchange-rates/use-exchange-rates';
import {
  getExternalLinkLabel,
  useGetExternalLink,
} from '../../../../../../../hooks/requests/use-get-external-link';
import { useInvoiceStatistics } from '../../../../../../../hooks/requests/use-invoices-statistics';
import { useDialog } from '../../../../../../../hooks/use-dialog';
import { formatCustomerSource } from '../../../../../../../utils/customer-source';
import { getCustomerInfoFields } from './customer-page-layout-consts/get-customer-info-fields';
import { CustomerCreditEntriesDialog } from './customer-page-layout-components/CustomerCreditEntriesDialog';
import { CustomerNavBackButton } from './customer-page-layout-components/CustomerNavBackButton';
import { CustomerNavIdentity } from './customer-page-layout-components/CustomerNavIdentity';
import { CustomerNavInfoFields } from './customer-page-layout-components/CustomerNavInfoFields';
import { CustomerNavMeta } from './customer-page-layout-components/CustomerNavMeta';
import { CustomerNavRevenue } from './customer-page-layout-components/CustomerNavRevenue';
import { CustomerRelatedEntities } from './customer-page-layout-components/CustomerRelatedEntities';
import { CustomersPageNavigationLayoutLoadingData } from './customer-page-layout-components/CustomersPageNavigationLayoutLoadingData';
import { useCustomerExternalLinkProvider } from './customer-page-layout-hooks/use-customer-external-link-provider';

export type CustomersPageNavigationLayoutProps = {
  customer?: Customer;
  relatedCustomers?: Customer[];
  onBackClick: () => void;
};

export const CustomersPageNavigationLayout: React.FC<CustomersPageNavigationLayoutProps> = ({
  customer,
  relatedCustomers,
  onBackClick,
}) => {
  const {
    isDialogOpen: isRelatedEntitiesDialogOpen,
    handleOpen: handleOpenRelatedEntitiesDialog,
    handleClose: handleCloseRelatedEntitiesDialog,
  } = useDialog();

  const {
    isDialogOpen: isCreditEntriesOverviewDialogOpen,
    handleOpen: handleOpenCreditEntriesOverviewDialog,
    handleClose: handleCloseCreditEntriesOverviewDialog,
  } = useDialog();

  const { data: creditLedgerOverview } = useCreditLedgerOverview(customer?.id ?? '', {
    enabled: isNotNullOrUndefined(customer),
  });
  const { data: invoicesStatistics } = useInvoiceStatistics(customer?.id);
  const { data: exchangeRates } = useExchangeRates();

  const activeProvider = useCustomerExternalLinkProvider(customer);

  const { data: externalLinkData } = useGetExternalLink(
    activeProvider,
    ENTITY_TYPES.CUSTOMER,
    customer?.id ?? '',
    { enabled: isNotNullOrUndefined(activeProvider) && isNotNullOrUndefined(customer) },
  );

  const currency = buildCurrencyLevel({ customerCurrency: customer?.settings?.currency });

  const lifecycleRevenue = invoicesStatistics?.summary?.totalAmount ?? 0;

  const availableBalance = useMemo(() => {
    if (
      isNullOrUndefined(creditLedgerOverview?.creditLedger?.balance) ||
      isNullOrUndefined(currency) ||
      isNullOrUndefined(exchangeRates)
    ) {
      return 0;
    }

    return convertCurrencyToUsd(creditLedgerOverview?.creditLedger?.balance, currency, exchangeRates);
  }, [creditLedgerOverview, currency, exchangeRates]);

  if (isNullOrUndefined(customer)) {
    return <CustomersPageNavigationLayoutLoadingData />;
  }

  const infoFields = getCustomerInfoFields(customer);
  const creditLedger = creditLedgerOverview?.creditLedger;
  const customerSource = formatCustomerSource(customer.source);
  const externalLinkLabel = activeProvider
    ? getExternalLinkLabel(activeProvider, ENTITY_TYPES.CUSTOMER)
    : null;

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: "center",
        padding: "12px 20px",
        flexShrink: 0,
        backgroundColor: '#FAFBFD',
        borderBottom: '1px solid #d6dbe7'
      }}>
      <CustomerNavBackButton onBackClick={onBackClick} />
      <CustomerNavIdentity customer={customer} />
      <CustomerNavMeta
        customerSource={customerSource}
        externalLinkUrl={externalLinkData?.url}
        externalLinkLabel={externalLinkLabel}
        relatedCustomers={relatedCustomers}
        onRelatedEntitiesClick={handleOpenRelatedEntitiesDialog}
      />
      <Stack
        direction="row"
        sx={{
          justifyContent: "flex-end",
          alignItems: "center",
          flexGrow: 1
        }}>
        <CustomerNavInfoFields infoFields={infoFields} />
        <CustomerNavRevenue
          lifecycleRevenue={lifecycleRevenue}
          availableBalance={availableBalance}
          entriesOverviewDisabled={isEmptyArray(creditLedger?.entriesOverviews)}
          onCreditEntriesClick={handleOpenCreditEntriesOverviewDialog}
        />
      </Stack>
      {isNotEmptyArray(relatedCustomers) && (
        <CustomerRelatedEntities
          isOpen={isRelatedEntitiesDialogOpen}
          onClose={handleCloseRelatedEntitiesDialog}
          children={relatedCustomers}
        />
      )}
      <CustomerCreditEntriesDialog
        creditLedgerEntries={creditLedger?.entriesOverviews ?? []}
        isOpen={isCreditEntriesOverviewDialogOpen}
        onClose={handleCloseCreditEntriesOverviewDialog}
      />
    </Stack>
  );
};
